import { useMutation } from "@apollo/client";
import {
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  InternalAdminEditUserMutation,
  InternalAdminEditUserMutationVariables,
  InternalAdminUserFragment,
} from "gql/graphql";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import colors from "app/colors";
import {
  CancelIcon,
  CheckIcon,
  CloseIcon,
  PencilOutlineIcon,
} from "modules/icons";
import { FieldValues, useForm } from "react-hook-form";
import {
  formatPhoneNumber,
  getMessageFromError,
  isValidPhoneNumberWithAreaCode,
} from "modules/utils/Functions";
import { isEmpty } from "lodash";
import useIsHovering from "modules/hooks/use-is-hovering";
import useGlobalEditing from "modules/hooks/use-global-editing";
import INTERNAL_ADMIN_EDIT_USER_MUTATION from "../_networking/internal-admin-edit-user";

interface IProps {
  user: InternalAdminUserFragment;
}

const InternalAdminEditUserPhone = ({ user }: IProps) => {
  const { users } = useUsers();
  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();

  const { mode, setMode } = useGlobalEditing(
    `InternalAdminEditUserPhone-${user.id}`,
  );

  const [internalAdminEditUserMutationAPI, { loading }] = useMutation<
    InternalAdminEditUserMutation,
    InternalAdminEditUserMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      phone: user?.phone || "",
    },
  });

  const onClearClicked = useCallback(() => {
    setValue("phone", "");
  }, [setValue]);

  const phone = watch("phone");

  const isDuplicatePhone = useMemo(
    () =>
      !isEmpty(phone) &&
      users.filter((_user) => _user.id !== user.id && _user.phone === phone)
        .length > 0,
    [phone, user.id, users],
  );

  useEffect(() => {
    if (isDuplicatePhone) {
      setError("phone", {
        message: "This phone number is already taken",
      });
    } else {
      clearErrors("phone");
    }
  }, [clearErrors, isDuplicatePhone, setError]);

  const onSubmit = useCallback(
    async ({ phone }: FieldValues) => {
      if (isEmpty(user.email) && isEmpty(phone)) {
        setError("phone", {
          message: "A user must have either an email or a phone number",
        });
        return;
      }
      internalAdminEditUserMutationAPI({
        variables: {
          organizationId: user.organizationId,
          id: user.id,
          input: {
            phone,
          },
        },
        onError: (error) => {
          setError("phone", {
            message: error.message,
          });
        },
        onCompleted: () => {
          setMode("display")();
        },
      });
    },
    [
      internalAdminEditUserMutationAPI,
      setError,
      setMode,
      user.email,
      user.id,
      user.organizationId,
    ],
  );

  if (!user) return null;

  if (mode === "edit") {
    return (
      <form
        id="admin-edit-user-phone-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <FormControl isInvalid={!!errors?.phone} w="full">
          <Stack w="full" spacing={0}>
            <HStack w="full" spacing={1}>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <Input
                  id="phone"
                  size="sm"
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("phone", {
                    validate: (value) =>
                      isEmpty(value) ||
                      isValidPhoneNumberWithAreaCode(value) ||
                      "Invalid phone number",
                  })}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear phone"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <IconButton
                aria-label="save"
                type="submit"
                icon={
                  loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <CheckIcon boxSize={4} fill={colors.white} />
                  )
                }
                bg={colors.blue}
                variant="hover"
                size="xs"
                onClick={setMode("edit")}
                isDisabled={loading || isDuplicatePhone}
              />
              <IconButton
                aria-label="cancel"
                icon={<CloseIcon boxSize={4} fill={colors.gray40} />}
                borderColor={colors.gray40}
                variant="outline"
                size="xs"
                onClick={setMode("display")}
                isDisabled={loading}
              />
            </HStack>
            <FormErrorMessage m={0}>
              {getMessageFromError(errors?.phone?.message)}
            </FormErrorMessage>
          </Stack>
        </FormControl>
      </form>
    );
  }

  if (isEmpty(user.phone)) {
    return (
      <Button
        variant="ghost"
        size="xxs"
        fontSize={12}
        fontWeight={500}
        color={colors.gray40}
        onClick={setMode("edit")}
      >
        + Add Phone
      </Button>
    );
  }

  return (
    <HStack
      w="full"
      spacing={4}
      borderRadius={4}
      py="6px"
      px={1}
      _hover={{
        backgroundColor: colors.blue20,
      }}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      <Text fontSize={14} fontWeight={500} color={colors.gray50} w="full">
        {formatPhoneNumber(user.phone)}
      </Text>
      <IconButton
        aria-label="Edit user name"
        icon={<PencilOutlineIcon boxSize={5} fill={colors.blue} />}
        variant="ghost"
        size="xxs"
        onClick={setMode("edit")}
        visibility={isHovering ? "visible" : "hidden"}
      />
    </HStack>
  );
};

export default memo(InternalAdminEditUserPhone);
